"use client";

import type { FormLabels } from "../types";

interface Data {
  solution_type: string;
  budget_range: string;
  timeframe: string;
  special_requirements: string;
}

interface Props {
  labels: FormLabels;
  data: Data;
  onChange: (d: Data) => void;
  onSubmit: (e: React.FormEvent) => void;
  onSkip: () => void;
  submitting: boolean;
}

const INPUT_CLS =
  "h-[52px] w-full rounded-card border-2 border-stone-100 bg-white px-4 text-base text-ink placeholder:text-stone-300 transition-colors focus:border-terracotta focus:outline-none";

/** Same base styling, but with an inline SVG chevron on the right for selects. */
const SELECT_CLS =
  INPUT_CLS +
  " appearance-none bg-[url('data:image/svg+xml;utf8,<svg xmlns=%22http://www.w3.org/2000/svg%22 width=%2218%22 height=%2218%22 viewBox=%220 0 24 24%22 fill=%22none%22 stroke=%22%238B8680%22 stroke-width=%222%22 stroke-linecap=%22round%22 stroke-linejoin=%22round%22><polyline points=%226 9 12 15 18 9%22/></svg>')] bg-[length:18px_18px] bg-[right_14px_center] bg-no-repeat pr-10";

const SOLUTIONS = [
  { v: "inloopdouche", l: "Inloopdouche" },
  { v: "bad-naar-douche", l: "Bad vervangen door douche" },
  { v: "instapbad", l: "Instapbad / zitbad" },
  { v: "badkamer-renovatie", l: "Volledige badkamer renovatie" },
  { v: "anders", l: "Anders / nog niet zeker" },
];

const BUDGETS = [
  { v: "onder_2500", l: "Onder €2.500" },
  { v: "2500_5000", l: "€2.500 – €5.000" },
  { v: "5000_10000", l: "€5.000 – €10.000" },
  { v: "boven_10000", l: "Boven €10.000" },
];

const TIMEFRAMES = [
  { v: "asap", l: "Zo snel mogelijk" },
  { v: "1_3_maanden", l: "Binnen 1–3 maanden" },
  { v: "3_6_maanden", l: "Binnen 3–6 maanden" },
  { v: "later", l: "Later dit jaar" },
  { v: "orientatie", l: "Nog in oriëntatiefase" },
];

export default function Step3Enrichment({
  labels,
  data,
  onChange,
  onSubmit,
  onSkip,
  submitting,
}: Props) {
  return (
    <form onSubmit={onSubmit} className="space-y-4" noValidate>
      <h2 className="font-display text-2xl md:text-3xl text-ink leading-tight mb-1">
        {labels.step_3_title}
      </h2>
      <p className="text-sm text-stone-700 mb-5">
        Hoe meer u deelt, hoe gerichter ons advies. Alles is optioneel.
      </p>

      <label className="block">
        <span className="text-sm font-medium text-ink block mb-1.5">
          {labels.solution_type_label}
        </span>
        <select
          value={data.solution_type}
          onChange={(e) => onChange({ ...data, solution_type: e.target.value })}
          className={SELECT_CLS}
        >
          <option value="">— Kies een optie —</option>
          {SOLUTIONS.map((s) => (
            <option key={s.v} value={s.v}>
              {s.l}
            </option>
          ))}
        </select>
      </label>

      <label className="block">
        <span className="text-sm font-medium text-ink block mb-1.5">
          {labels.budget_range_label}
        </span>
        <select
          value={data.budget_range}
          onChange={(e) => onChange({ ...data, budget_range: e.target.value })}
          className={SELECT_CLS}
        >
          <option value="">— Kies een optie —</option>
          {BUDGETS.map((s) => (
            <option key={s.v} value={s.v}>
              {s.l}
            </option>
          ))}
        </select>
      </label>

      <label className="block">
        <span className="text-sm font-medium text-ink block mb-1.5">
          {labels.timeframe_label}
        </span>
        <select
          value={data.timeframe}
          onChange={(e) => onChange({ ...data, timeframe: e.target.value })}
          className={SELECT_CLS}
        >
          <option value="">— Kies een optie —</option>
          {TIMEFRAMES.map((s) => (
            <option key={s.v} value={s.v}>
              {s.l}
            </option>
          ))}
        </select>
      </label>

      <label className="block">
        <span className="text-sm font-medium text-ink block mb-1.5">
          {labels.special_requirements_label}
        </span>
        <textarea
          value={data.special_requirements}
          onChange={(e) => onChange({ ...data, special_requirements: e.target.value })}
          rows={3}
          className="w-full rounded-card border-2 border-stone-100 bg-white p-4 text-base text-ink placeholder:text-stone-300 transition-colors focus:border-terracotta focus:outline-none"
          placeholder="Bijv. mindervalide, lage drempel nodig, specifieke wensen…"
        />
      </label>

      <button
        type="submit"
        disabled={submitting}
        className="w-full rounded-pill bg-terracotta text-white font-semibold py-4 text-base shadow-card hover:bg-terracotta-700 hover:shadow-card-hover disabled:opacity-40 transition-all mt-2 focus:outline-none focus-visible:shadow-focus"
      >
        {submitting ? "Verzenden…" : labels.submit_step_3}
      </button>

      <button
        type="button"
        onClick={onSkip}
        className="block mx-auto text-sm text-stone-700 hover:text-ink underline underline-offset-2 mt-2 focus:outline-none focus-visible:shadow-focus rounded"
      >
        Overslaan en afronden
      </button>
    </form>
  );
}
